<template>
  <v-row no-gutters class="pt-8 pr-5">
    <v-col>
      <card-title-row
        translationPath="sidebar.zdmLamp.addLamp"
        :has-back-button="true"
        @back="goBack"
        class="pb-6"
        align-class="align-center"
      >
      </card-title-row>
      <v-form v-model="isFormValid">
        <sidebar-form
          :is-editing-on="true"
          :inputs="inputs"
          v-model="payload"
          ref="form"
          @appendIconClicked="handleAppendIconClick"
        >
        </sidebar-form>
      </v-form>
    </v-col>
    <zdm-actions-dialog
      v-if="isDialogLoaded"
      :is-visible.sync="isDialogVisible"
      :actions="zdmActions"
    ></zdm-actions-dialog>
  </v-row>
</template>

<script>
import { get, sync, call } from 'vuex-pathify';

import { prepareDrawnGeometry } from '@/assets/js/mapUtils';
import SidebarForm from '@/components/SidebarForm';
import CardTitleRow from '@/components/CardTitleRow';

export default {
  name: 'AddingLamp',
  components: {
    SidebarForm,
    CardTitleRow,
    ZdmActionsDialog: () => import('@/components/ZDMActionsDialog'),
  },
  data: vm => ({
    isDialogLoaded: false,
    isDialogVisible: false,
    payload: {
      pattern_id: undefined,
      status: vm.$i18n.t('sidebar.zdmLamp.published'),
      action_id: undefined,
    },
    isFormValid: false,
  }),
  computed: {
    isAddingButtonActive: sync('edit/isAddingButtonActive'),
    isCopyButtonActive: sync('edit/isCopyButtonActive'),
    copiedObject: sync('edit/copiedObject'),
    newFeatureGeometry: sync('edit/newFeatureGeometry'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
    zdmActions: sync('zdm_data/zdmActions'),
    zdmPatterns: sync('zdm_data/lanternPatterns'),
    streetLampsUnpublishedLayerId: get('admin/modulesMapping@zdm_data.layers.street_lamp_unpublished'),
    streetLampsLayerId: get('admin/modulesMapping@zdm_data.layers.street_lamp'),
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    layers: get('layers/layers'),
    zdmActionsPrepared() {
      return this.prepareItems(this.zdmActions);
    },
    zdmPatternsPrepared() {
      return this.prepareItems(this.zdmPatterns);
    },
    isValid() {
      return this.isFormValid && this.newFeatureGeometry;
    },
    inputs() {
      return [
        ...(this.isCopyButtonActive
          ? []
          : [
              {
                icon: 'mdi-tag-multiple',
                name: 'pattern_id',
                dataType: {
                  name: 'select',
                },
                items: this.zdmPatternsPrepared,
                translationPath: 'sidebar.zdmLamp.pattern',
                cols: '6',
                nameCols: '5',
                rules: [v => !!v || v == '0' || 'rules.required'],
              },
              {
                icon: 'mdi-crane',
                name: 'action_id',
                dataType: {
                  name: 'select',
                },
                items: this.zdmActionsPrepared,
                translationPath: 'sidebar.zdmLamp.action',
                cols: '6',
                appendIcon: 'mdi-cog',
                nameCols: '5',
                appendIconTooltip: this.$i18n.t('sidebar.zdmLamp.manageActions'),
                rules: [v => !!v || v == '0' || 'rules.required'],
              },
            ]),
        {
          icon: 'mdi-progress-check',
          name: 'status',
          dataType: {
            name: 'select',
          },
          items: [this.$i18n.t('sidebar.zdmLamp.published'), this.$i18n.t('sidebar.zdmLamp.toBePublished')],
          translationPath: 'sidebar.zdmLamp.status',
          cols: '6',
          nameCols: '5',
          rules: [v => !!v || v == '0' || 'rules.required'],
          hint: this.statusHint,
          persistentHint: true,
        },
      ];
    },
    layerId() {
      return this.$route.params.lid;
    },
    isSelectedLayerActive() {
      return this.layerId == this.selectedLayerId;
    },
    selectedLayerId() {
      return this.dict[this.payload.status];
    },
    statusHint() {
      return !this.isSelectedLayerActive
        ? this.$i18n.t('sidebar.zdmLamp.addedToOtherLayer', { layer: this.payload.status })
        : '';
    },
    dict() {
      return {
        [this.$i18n.t('sidebar.zdmLamp.published')]: this.streetLampsLayerId,
        [this.$i18n.t('sidebar.zdmLamp.toBePublished')]: this.streetLampsUnpublishedLayerId,
      };
    },
    isLayerInProject() {
      return this.projectLayers.some(l => l.id == this.selectedLayerId);
    },
  },
  watch: {
    isValid(nV) {
      this.isStickyButtonsValid = nV;
    },
  },
  methods: {
    getActions: call('zdm_data/getActions'),
    getPatterns: call('zdm_data/getPatterns'),
    addLantern: call('zdm_data/addLantern'),
    copyLantern: call('zdm_data/copyLantern'),
    handleAppendIconClick(name) {
      if (name === 'action_id') {
        this.openActionsDialog();
      }
    },
    openActionsDialog() {
      this.isDialogLoaded = true;
      this.isDialogVisible = true;
    },
    prepareItems(items) {
      return (items || []).map(item => ({ text: item.name, value: item.id }));
    },
    attachListeners() {
      this.$root.$on('add', this.onAddClicked);
      this.$root.$on('cancel', this.cancel);
    },
    dettachListeners() {
      this.$root.$off('add');
      this.$root.$off('cancel');
    },
    refreshLayersAndTable(layers) {
      this.$root.$emit('refreshTableData');
      this.$root.$emit(
        'refreshMvtSources',
        layers.map(layer => ({ id: layer, group: 'layers' })),
        true
      );
    },
    addProjectLayer(layer) {
      this.$root.$emit('addLayersHandler', [layer]);
    },
    onAddClicked() {
      if (this.isCopyButtonActive) {
        this.copyLamp();
      } else {
        this.addLamp();
      }
    },
    onSuccess(id) {
      this.refreshHandler();
      this.pushNewRoute(id);
      this.isStickyButtonsLoading = false;
      this.isAddingButtonActive = false;
      this.isCopyButtonActive = false;
      this.copiedObject = undefined;
      this.$root.$emit('deleteSidebarGeometry');
    },
    async copyLamp() {
      try {
        this.isStickyButtonsLoading = true;
        const r = await this.copyLantern({
          id: this.copiedObject,
          body: { ...this.payload, geometry: this.newFeatureGeometry },
        });
        this.onSuccess(r.data.data.street_lamp_id);
      } catch (error) {
        console.log('  error', error);
        this.isStickyButtonsLoading = false;
      }
    },
    pushNewRoute(id) {
      this.$router.push({
        name: 'layerFeature',
        params: { lid: this.selectedLayerId, fid: id },
      });
    },
    refreshHandler() {
      if (this.isLayerInProject) {
        this.refreshLayersAndTable([this.selectedLayerId]);
      } else {
        this.addProjectLayer(this.layers[this.selectedLayerId]);
      }
    },
    async addLamp() {
      try {
        this.isStickyButtonsLoading = true;
        const r = await this.addLantern({ ...this.payload, geometry: this.newFeatureGeometry });
        this.onSuccess(r.data.data.street_lamp_id);
      } catch (error) {
        console.log('  error', error);
        this.isStickyButtonsLoading = false;
      } finally {
        this.isStickyButtonsLoading = false;
      }
    },
    cancel() {
      this.goBack();
    },
    async fetchData() {
      try {
        await Promise.all([this.getActions(), this.getPatterns()]);
      } catch (error) {
        console.log('  error', error);
      }
    },
    async onInit() {
      this.stickyButtons = [
        {
          action: 'add',
          translationPath: 'dialog.agreeAdd',
        },
        {
          action: 'cancel',
          cancel: true,
          translationPath: 'dialog.disagree',
        },
      ];
      this.isStickyButtonsVisible = true;
      this.attachListeners();
      await this.fetchData();
      this.$root.$emit('modifySidebarGeometry', {
        geometryValue: this.newFeatureGeometry,
        modifyendCallback: this.onModifyend,
        layerId: this.layerId,
      });
    },
    onModifyend(e) {
      this.setSidebarGeometry(e.features.getArray()[0]);
    },
    setSidebarGeometry(feature) {
      if (!feature) {
        this.newFeatureGeometry = null;
      } else {
        this.newFeatureGeometry = prepareDrawnGeometry(feature);
      }
    },
    goBack() {
      this.$router.push({ name: 'layerTable', params: { layerId: this.$route.params.lid } });
    },
  },
  mounted() {
    this.onInit();
  },
  beforeDestroy() {
    this.isStickyButtonsVisible = false;
    this.stickyButtons = [];
    this.isCopyButtonActive = false;
    this.isAddingButtonActive = false;
    this.dettachListeners();
    this.$root.$emit('deleteSidebarGeometry');
  },
};
</script>

<style lang="scss" scoped></style>
